import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Flex, } from "@chakra-ui/react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { breathalyzersService } from "services/breathalyzersService";
import { LOCALE_PT_BR } from "constants/date-time";
import { ROUTES } from "constants/routes-path";

function BreathalyzersList() {
  const { t} = useTranslation();
  const [searchParams, setSearchParams] = useState({});
  const [response, setResponse] = useState(undefined);
  const [selectedRow, setSelectedRow] = useState(undefined);
  const dialogRef = useRef(undefined);
  const showToast = useCustomToast();
  const navigate = useNavigate();

  const columns = useMemo(() => [
    { Header: t("settings.breathalyzer.list.table.header.brand"), accessor: "marca" },
    { Header: t("settings.breathalyzer.list.table.header.model"), accessor: "modelo" },
    { Header: t("settings.breathalyzer.list.table.header.number"), accessor: "numero_serie" },
    {
      Header: t("settings.breathalyzer.list.table.header.date"),
      accessor: "data_criacao",
      custom: ({ value }) => value && new Date(value).toLocaleDateString(LOCALE_PT_BR),
    },
    {
      Header: t("system.labels.actions"),
      disableSortBy: true,
      actions: [
        {
          label: t("system.actions.edit"),
          permission: "editar-pessoa",
          icon: <FaEdit />,
          onClick: ({ original }) => navigate(ROUTES.breathalyzersEditPath.replace(":id", original.id)),
        },
        {
          label: t("system.actions.remove"),
          permission: "editar-pessoa",
          icon: <FaTrashAlt color="#E53E3E" />,
          onClick: ({ original }) => handleRemove(original),
        }
      ],
    },
  ], []);

  const newBreathalyzer = () => {
    navigate(ROUTES.breathalyzersAddPath);
  }

  const handleRemove = (row) => {
    setSelectedRow(row);
    dialogRef.current?.displayDialog({
      title: t("settings.breathalyzer.list.actions.remove-breathalyzer"),
      message: t("system.actions.remove-confirm"),
    });
  };

  const handleConfirmRemove = async () => {
    try {
      await breathalyzersService.delete(selectedRow.id);
      showToast({ title: t("settings.breathalyzer.messages.success.delete"), status: "success" });
      await fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  const fetchData = async (params = {}) => {
    if (response) setResponse(undefined);

    try {
      params = { ...searchParams, ...params };
      setSearchParams(params);
      const data = await breathalyzersService.list(params);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      showToast({ title: t(err), status: "error" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Flex gap="5" direction="column" pt={{ base: "150px", lg: "90px" }}>
      <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
        <Button
          variant="list-primary"
          leftIcon={<AddIcon fontSize="sm" />}
          alignSelf={{ sm: "flex-start", lg: undefined }}
          onClick={newBreathalyzer}
        >
          {t("settings.breathalyzer.list.actions.new-breathalyzer")}
        </Button>
      </Flex>

      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("settings.breathalyzer.title")} subtitle={t("settings.breathalyzer.description")} />

        <CardBody>
          {response ? (
            <SearchTable2
              columnsData={columns}
              tableData={response.content}
              pageableData={toTablePageableData(response, { defaultField: "marca", ...searchParams })}
              onChange={fetchData}
            />
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>

      <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
    </Flex>
  );
}

export default BreathalyzersList;
